// Aparelhos e telas desenhados em `em`: o tamanho do conjunto vem do fontSize de quem envolve
import type { ReactNode } from 'react';
import { Box } from '@mui/material';
import { PRIMARY } from '../../theme/palette';

const MOLDURA = '#1b1f24';
const MOLDURA_CLARA = '#2c333b';
const TEXTO = '#23272b';
const TEXTO_FRACO = '#80868b';
const FUNDO_FOTO = '#fdecf2';
const AVISO = '#ed6c02';
const SUCESSO = '#2e7d32';
// O anel claro separa a moldura escura do fundo no modo dark
const SOMBRA = '0 0 0 0.12em rgba(255, 255, 255, 0.14), 0 2.4em 5em -1.6em rgba(0, 0, 0, 0.45)';

const icone = (nome: string): string => `/images/groups-icons/${nome}.svg`;

interface TextoProps {
  t: string;
  s?: number;
  w?: number;
  c?: string;
}

function Texto({ t, s = 1, w = 400, c = TEXTO }: TextoProps): JSX.Element {
  return (
    <Box
      component="span"
      sx={{
        display: 'block',
        fontSize: `${s}em`,
        fontWeight: w,
        color: c,
        lineHeight: 1.25,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {t}
    </Box>
  );
}

function Foto({ nome, tam }: { nome: string; tam: number }): JSX.Element {
  return (
    <Box
      sx={{
        width: `${tam}em`,
        height: `${tam}em`,
        flexShrink: 0,
        borderRadius: '0.8em',
        bgcolor: FUNDO_FOTO,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box component="img" src={icone(nome)} alt="" sx={{ width: '65%', height: '65%' }} />
    </Box>
  );
}

function Pilula({ t, ativo = false }: { t: string; ativo?: boolean }): JSX.Element {
  return (
    <Box
      sx={{
        px: '0.9em',
        py: '0.35em',
        borderRadius: '2em',
        flexShrink: 0,
        bgcolor: ativo ? PRIMARY : '#f1f2f4',
      }}
    >
      <Texto t={t} s={0.9} w={600} c={ativo ? '#fff' : TEXTO_FRACO} />
    </Box>
  );
}

function BotaoContorno({ t, cor }: { t: string; cor: string }): JSX.Element {
  return (
    <Box sx={{ px: '0.9em', py: '0.4em', borderRadius: '0.6em', border: `0.12em solid ${cor}` }}>
      <Texto t={t} s={0.85} w={700} c={cor} />
    </Box>
  );
}

function BarraAcao({ esquerda, direita, s = 1 }: { esquerda: string; direita: string; s?: number }): JSX.Element {
  return (
    <Box
      sx={{
        mx: '1em',
        mb: '1.2em',
        mt: 'auto',
        px: '1.2em',
        py: '0.9em',
        borderRadius: '1em',
        bgcolor: PRIMARY,
        display: 'flex',
        justifyContent: 'space-between',
        boxShadow: '0 0.6em 1.4em -0.4em rgba(241, 11, 92, 0.6)',
      }}
    >
      <Texto t={esquerda} s={s} w={700} c="#fff" />
      <Texto t={direita} s={s} w={700} c="#fff" />
    </Box>
  );
}

/* ---------- aparelhos ---------- */

export function Celular({ children }: { children: ReactNode }): JSX.Element {
  return (
    <Box sx={{ width: '24em', height: '49em', p: '0.7em', borderRadius: '3.6em', bgcolor: MOLDURA, boxShadow: SOMBRA, flexShrink: 0 }}>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          borderRadius: '2.9em',
          overflow: 'hidden',
          bgcolor: '#fff',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '0.8em',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '7em',
            height: '2em',
            borderRadius: '1em',
            bgcolor: MOLDURA,
            zIndex: 2,
          }}
        />
        {children}
      </Box>
    </Box>
  );
}

export function Tablet({ children }: { children: ReactNode }): JSX.Element {
  return (
    <Box sx={{ width: '44em', height: '30em', p: '1.1em', borderRadius: '2.2em', bgcolor: MOLDURA, boxShadow: SOMBRA, flexShrink: 0 }}>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: '1.1em',
          overflow: 'hidden',
          bgcolor: '#f7f8fa',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

export function Totem({ children }: { children: ReactNode }): JSX.Element {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
      <Box sx={{ width: '25em', height: '41em', p: '0.9em', borderRadius: '1.6em 1.6em 0 0', bgcolor: MOLDURA, boxShadow: SOMBRA }}>
        <Box
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: '0.8em',
            overflow: 'hidden',
            bgcolor: '#fff',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {children}
        </Box>
      </Box>
      <Box
        sx={{
          width: '25em',
          height: '5em',
          bgcolor: MOLDURA_CLARA,
          borderRadius: '0 0 1.2em 1.2em',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '2em',
        }}
      >
        <Box sx={{ width: '6em', height: '0.5em', borderRadius: '0.3em', bgcolor: MOLDURA }} />
        <Box sx={{ width: '3.4em', height: '2.4em', borderRadius: '0.4em', bgcolor: MOLDURA }} />
      </Box>
      <Box sx={{ width: '6em', height: '9em', background: `linear-gradient(90deg, ${MOLDURA}, ${MOLDURA_CLARA}, ${MOLDURA})` }} />
      <Box sx={{ width: '17em', height: '1.6em', borderRadius: '1em 1em 0.4em 0.4em', bgcolor: MOLDURA, boxShadow: SOMBRA }} />
    </Box>
  );
}

// Padrão de QR ilustrativo, não codifica nada
function QrCode(): JSX.Element {
  const n = 21;
  const ehLocalizador = (x: number, y: number): boolean => (x < 7 && y < 7) || (x >= n - 7 && y < 7) || (x < 7 && y >= n - 7);
  const celulas: JSX.Element[] = [];

  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      if (ehLocalizador(x, y)) continue;
      if ((x * 7 + y * 13 + x * y) % 5 < 2) celulas.push(<rect key={`${x}-${y}`} x={x} y={y} width={1} height={1} />);
    }
  }

  const localizador = (x: number, y: number): JSX.Element => (
    <g key={`l-${x}-${y}`}>
      <rect x={x} y={y} width={7} height={7} />
      <rect x={x + 1} y={y + 1} width={5} height={5} fill="#fff" />
      <rect x={x + 2} y={y + 2} width={3} height={3} />
    </g>
  );

  return (
    <Box component="svg" viewBox={`0 0 ${n} ${n}`} sx={{ width: '11em', height: '11em', fill: TEXTO, shapeRendering: 'crispEdges' }}>
      {celulas}
      {localizador(0, 0)}
      {localizador(n - 7, 0)}
      {localizador(0, n - 7)}
    </Box>
  );
}

export function PlaquinhaMesa({ mesa }: { mesa: string }): JSX.Element {
  return (
    <Box
      sx={{
        width: '17em',
        pt: '1.6em',
        pb: '2em',
        px: '1.6em',
        borderRadius: '1.2em',
        bgcolor: '#fff',
        borderTop: `0.8em solid ${PRIMARY}`,
        boxShadow: SOMBRA,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '1em',
        textAlign: 'center',
        flexShrink: 0,
      }}
    >
      <Texto t={mesa} s={1.8} w={800} />
      <QrCode />
      <Box sx={{ width: '100%' }}>
        <Texto t="Aponte a câmera" s={1.05} w={700} />
        <Texto t="e peça por aqui" s={1.05} c={TEXTO_FRACO} />
      </Box>
    </Box>
  );
}

/* ---------- telas ---------- */

const PRODUTOS = [
  { nome: 'X-Bacon da casa', desc: 'Pão brioche, blend 180g', preco: 'R$ 32,90', foto: 'hamburguer' },
  { nome: 'Batata rústica', desc: 'Com alecrim e páprica', preco: 'R$ 18,00', foto: 'batataFrita' },
  { nome: 'Pizza marguerita', desc: 'Molho, muçarela e manjericão', preco: 'R$ 54,00', foto: 'pizzas' },
  { nome: 'Refrigerante lata', desc: '350 ml', preco: 'R$ 6,50', foto: 'refrigerantes' },
];

export function TelaDelivery(): JSX.Element {
  return (
    <>
      <Box sx={{ position: 'relative', height: '11em', flexShrink: 0, background: `linear-gradient(135deg, ${PRIMARY}, #ff7aa5)` }}>
        <Box
          sx={{
            position: 'absolute',
            left: '1.4em',
            bottom: '-2.4em',
            width: '5em',
            height: '5em',
            borderRadius: '50%',
            bgcolor: '#fff',
            boxShadow: '0 0.4em 1em rgba(0,0,0,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box component="img" src={icone('hamburguer')} alt="" sx={{ width: '3em' }} />
        </Box>
      </Box>

      <Box sx={{ px: '1.4em', pt: '3em' }}>
        <Texto t="Hamburgueria da Praça" s={1.35} w={800} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.6em', mt: '0.3em' }}>
          <Box sx={{ width: '0.6em', height: '0.6em', borderRadius: '50%', bgcolor: SUCESSO, flexShrink: 0 }} />
          <Texto t="Aberto · 30–45 min · Entrega R$ 5,00" s={0.9} c={TEXTO_FRACO} />
        </Box>

        <Box sx={{ display: 'flex', mt: '1.2em', p: '0.3em', borderRadius: '0.8em', bgcolor: '#f1f2f4' }}>
          <Box sx={{ flex: 1, py: '0.4em', borderRadius: '0.6em', bgcolor: '#fff', textAlign: 'center', boxShadow: '0 0.1em 0.3em rgba(0,0,0,0.1)' }}>
            <Texto t="Entrega" s={0.95} w={700} />
          </Box>
          <Box sx={{ flex: 1, py: '0.4em', textAlign: 'center' }}>
            <Texto t="Retirada" s={0.95} w={600} c={TEXTO_FRACO} />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: '0.5em', mt: '1.2em', overflow: 'hidden' }}>
          <Pilula t="Lanches" ativo />
          <Pilula t="Porções" />
          <Pilula t="Pizzas" />
          <Pilula t="Bebidas" />
        </Box>
      </Box>

      <Box sx={{ px: '1.4em', mt: '1.2em', display: 'flex', flexDirection: 'column', gap: '1em' }}>
        {PRODUTOS.slice(0, 3).map((p) => (
          <Box key={p.nome} sx={{ display: 'flex', gap: '1em', alignItems: 'center' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Texto t={p.nome} s={1.05} w={700} />
              <Texto t={p.desc} s={0.85} c={TEXTO_FRACO} />
              <Box sx={{ mt: '0.3em' }}>
                <Texto t={p.preco} s={1} w={700} c={PRIMARY} />
              </Box>
            </Box>
            <Foto nome={p.foto} tam={5.4} />
          </Box>
        ))}
      </Box>

      <BarraAcao esquerda="Ver carrinho · 2" direita="R$ 58,90" />
    </>
  );
}

const CATEGORIAS_TOTEM = [
  { nome: 'Lanches', foto: 'hamburguer' },
  { nome: 'Pizzas', foto: 'pizzas' },
  { nome: 'Porções', foto: 'batataFrita' },
  { nome: 'Bebidas', foto: 'refrigerantes' },
  { nome: 'Sobremesas', foto: 'sorvetes' },
  { nome: 'Combos', foto: 'combo' },
];

export function TelaTotem(): JSX.Element {
  return (
    <>
      <Box sx={{ px: '1.6em', pt: '1.8em', pb: '1.4em', background: `linear-gradient(160deg, ${PRIMARY}, #ff5f93)` }}>
        <Texto t="Olá! O que vai ser hoje?" s={1.5} w={800} c="#fff" />
        <Texto t="Toque numa categoria para começar" s={0.95} c="rgba(255,255,255,0.85)" />
      </Box>

      <Box sx={{ p: '1.2em', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.9em' }}>
        {CATEGORIAS_TOTEM.map((c, i) => (
          <Box
            key={c.nome}
            sx={{
              py: '1em',
              borderRadius: '1em',
              bgcolor: i === 0 ? FUNDO_FOTO : '#f7f8fa',
              border: i === 0 ? `0.15em solid ${PRIMARY}` : '0.15em solid transparent',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.5em',
            }}
          >
            <Box component="img" src={icone(c.foto)} alt="" sx={{ width: '3.6em', height: '3.6em' }} />
            <Texto t={c.nome} s={1} w={700} />
          </Box>
        ))}
      </Box>

      <BarraAcao esquerda="Meu pedido · 3" direita="R$ 45,80" s={1.05} />
    </>
  );
}

const CATEGORIAS_TABLET = ['hamburguer', 'pizzas', 'batataFrita', 'refrigerantes', 'sobremesas'];

export function TelaTablet(): JSX.Element {
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '1em', px: '1.4em', py: '0.9em', bgcolor: '#fff', borderBottom: '0.1em solid #eceef1' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Texto t="Bistrô Central" s={1.25} w={800} />
        </Box>
        <Box sx={{ px: '0.8em', py: '0.3em', borderRadius: '0.5em', bgcolor: '#f1f2f4' }}>
          <Texto t="Mesa 12" s={0.9} w={700} />
        </Box>
        <BotaoContorno t="Chamar garçom" cor={AVISO} />
        <BotaoContorno t="Conta" cor={SUCESSO} />
      </Box>

      <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <Box sx={{ width: '6.4em', py: '0.8em', bgcolor: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.6em' }}>
          {CATEGORIAS_TABLET.map((c, i) => (
            <Box
              key={c}
              sx={{
                width: '4.2em',
                height: '3.6em',
                borderRadius: '0.8em',
                bgcolor: i === 0 ? FUNDO_FOTO : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box component="img" src={icone(c)} alt="" sx={{ width: '2.4em', height: '2.4em' }} />
            </Box>
          ))}
        </Box>

        <Box sx={{ flex: 1, p: '1.1em', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.9em', alignContent: 'start' }}>
          {[...PRODUTOS.slice(0, 3), PRODUTOS[3]!, PRODUTOS[1]!, PRODUTOS[0]!].map((p, i) => (
            <Box key={`${p.nome}-${i}`} sx={{ p: '0.7em', borderRadius: '0.9em', bgcolor: '#fff', boxShadow: '0 0.2em 0.6em rgba(0,0,0,0.06)' }}>
              <Box sx={{ height: '4.2em', borderRadius: '0.6em', bgcolor: FUNDO_FOTO, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box component="img" src={icone(p.foto)} alt="" sx={{ height: '3em' }} />
              </Box>
              <Box sx={{ mt: '0.5em' }}>
                <Texto t={p.nome} s={0.9} w={700} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: '0.2em' }}>
                <Texto t={p.preco} s={0.9} w={700} c={PRIMARY} />
                <Box sx={{ width: '1.6em', height: '1.6em', borderRadius: '50%', bgcolor: PRIMARY, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Texto t="+" s={1.1} w={800} c="#fff" />
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </>
  );
}

const ITENS_MESA = [
  { qtd: 2, nome: 'Chopp 300 ml', preco: 'R$ 23,80' },
  { qtd: 1, nome: 'Porção de fritas', preco: 'R$ 28,00' },
  { qtd: 1, nome: 'Pizza marguerita', preco: 'R$ 54,00' },
];

export function TelaMesaQr(): JSX.Element {
  return (
    <>
      <Box sx={{ px: '1.4em', pt: '3.6em', pb: '1.2em', bgcolor: '#f7f8fa' }}>
        <Texto t="Mesa 7" s={1.6} w={800} />
        <Texto t="Pedido direto pelo celular, sem app" s={0.9} c={TEXTO_FRACO} />
      </Box>

      <Box sx={{ px: '1.4em', pt: '1.2em' }}>
        <Texto t="Seu pedido" s={1.05} w={800} />
        <Box sx={{ mt: '0.8em', display: 'flex', flexDirection: 'column', gap: '0.9em' }}>
          {ITENS_MESA.map((i) => (
            <Box key={i.nome} sx={{ display: 'flex', alignItems: 'center', gap: '0.8em' }}>
              <Box sx={{ width: '2em', height: '2em', flexShrink: 0, borderRadius: '0.5em', bgcolor: FUNDO_FOTO, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Texto t={`${i.qtd}x`} s={0.8} w={800} c={PRIMARY} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Texto t={i.nome} s={0.95} w={600} />
              </Box>
              <Texto t={i.preco} s={0.95} w={700} />
            </Box>
          ))}
        </Box>

        <Box sx={{ mt: '1.2em', pt: '1em', borderTop: '0.1em dashed #d9dce0', display: 'flex', justifyContent: 'space-between' }}>
          <Texto t="Total" s={1.05} w={700} />
          <Texto t="R$ 105,80" s={1.05} w={800} />
        </Box>
      </Box>

      <BarraAcao esquerda="Enviar para a cozinha" direita="→" />
    </>
  );
}
